import { useEffect, useMemo, useRef } from "react";
import { fetchAttachmentBlob } from "@/api/taskAttachments";

/**
 * D-147 W1 S3 — рендер server-derived contentHtml сторінки «Бази знань».
 *
 * HTML приходить із власного whitelist-рендерера S1 (§7): алфавіт вузлів
 * фіксований (p/h2/h3/ul/ol/li/strong/em/a/img), текст ескейпнутий, img src =
 * ЛИШЕ внутрішні /api/v1/Custom/knowledge/…/images/… власного item-а —
 * innerHTML тут безпечний by construction.
 *
 * Trap (D-131): bare <img src="/api/v1/…"> у цьому деплої = 401 (auth =
 * header-токен, не cookie). Тому ДО рендера src → data-kb-src (жодного
 * 401-пострілу браузера), а ефект тягне кожен бінарник авторизованим blob-ом
 * і підставляє object-URL (revoke на cleanup — без blob-leak).
 */
export function KbContentHtml({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);

  const prepared = useMemo(
    () =>
      html.replace(
        /<img src="(\/api\/v1\/Custom\/knowledge\/[^"]+)"/g,
        '<img data-kb-src="$1"',
      ),
    [html],
  );

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    let active = true;
    const objectUrls: string[] = [];

    root.querySelectorAll<HTMLImageElement>("img[data-kb-src]").forEach((img) => {
      const src = img.getAttribute("data-kb-src");
      if (!src) return;
      img.classList.add("kb-img-loading");
      fetchAttachmentBlob(src)
        .then((blob) => {
          if (!active) return;
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          img.src = url;
          img.classList.remove("kb-img-loading");
        })
        .catch(() => {
          // деny/збій → alt-текст без битого запиту (generic-404-семантика P12)
          img.classList.remove("kb-img-loading");
        });
    });

    return () => {
      active = false;
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [prepared]);

  return (
    <div
      ref={ref}
      className="max-w-[760px] text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_h2]:mb-1.5 [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:mb-1.5 [&_h3]:mt-4 [&_h3]:text-[15px] [&_h3]:font-bold [&_img]:my-2.5 [&_img]:max-w-full [&_img]:rounded-lg [&_img]:border [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-1.5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 first:[&_h2]:mt-0"
      dangerouslySetInnerHTML={{ __html: prepared }}
    />
  );
}
